import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaMoneyBillWave,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import BankView from "./BankView";

const BankListChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [data, setData] = useState([]);

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/banks/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        //console.log(JSON.stringify(response.data.users));
        const oldData = response.data.banks;
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data?._id,
            bank_name: data?.bank_name,
            account: data?.account_number,
            account_type: data?.account_type,
            branch: data?.branch,
            active_status: data?.active_status,
            payment_gateway_status: data?.payment_gateway_status,
          };
        });

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err.response.data.message);
      });
  };

  useEffect(() => {
    const getDataFromServer = () => {
      let userToken = localStorage.getItem("token");

      let urlLink = `${process.env.REACT_APP_API_URL}/banks/${user.church_id}`;

      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };

      axios(config)
        .then(function (response) {
          //console.log(JSON.stringify(response.data));
          const oldData = response.data.banks;
          const updatedData = oldData.map((data) => {
            return {
              ...data,
              id: data?._id,
              bank_name: data?.bank_name,
              account: data?.account_number,
              account_type: data?.account_type,
              branch: data?.branch,
              active_status: data?.active_status,
              payment_gateway_status: data?.payment_gateway_status,
            };
          });

          setData(updatedData);
        })
        .catch(function (err) {
          console.log("error api call ", err?.response?.data);
        });
    };
    getDataFromServer();
  }, []);

  const handleDelete = (row) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this Bank?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = async (row) => {
    const userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/banks/delete/${row._id}`,
        config
      );
      toast.success("Bank Deleted Successfully.");
      getDataFromServer();
    } catch (err) {
      errorLogApi(err, "BankListChild", "/banks");
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const filteredData = useMemo(() => {
    return data.filter((bank) =>
      Object.values(bank).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Bank List</h2>
        {user?.is_a_trustee === true && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<FaPlus />}
            onClick={() => navigate("/bank/add")}
          >
            Add Bank
          </Button>
        )}
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search banks..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Bank Name</TableCell>
              <TableCell>Account Number</TableCell>
              <TableCell>Account Type</TableCell>
              {/* <TableCell>Branch</TableCell> */}
              <TableCell>Status</TableCell>
              <TableCell>Gateway</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row._id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          toggleModal();
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                    {user?.is_a_trustee === true && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate("/bank/update", { state: row })
                          }
                        >
                          <FaEdit className="text-gray-600" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Balances">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/bank/balance", { state: row })
                        }
                      >
                        <FaMoneyBillWave className="text-green-600" />
                      </IconButton>
                    </Tooltip>
                    {user?.is_a_trustee === true && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row)}
                        >
                          <FaTrash className="text-red-600" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell>{row.bank_name}</TableCell>
                <TableCell>{row.account_number}</TableCell>
                <TableCell>{row.account_type}</TableCell>
                {/* <TableCell>{row.branch}</TableCell> */}
                <TableCell>
                  <span
                    className={`inline-block px-2 py-1 text-xs font-semibold ${
                      row.active_status
                        ? "bg-green-400 text-gray-800"
                        : "bg-orange-400 text-white"
                    } rounded-full`}
                  >
                    {row.active_status ? "Enabled" : "Disabled"}
                  </span>
                </TableCell>
                <TableCell>
                  {row.payment_gateway_status ? (
                    <span className="text-green-600">✓</span>
                  ) : (
                    <span className="text-red-600">✗</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/banks" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <BankView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default BankListChild;
